import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, Video, ChevronDown, User, ArrowLeft } from 'lucide-react';
import { ref, getDownloadURL } from "firebase/storage";
import { storage, auth, signOut, getDoc, updateDoc, doc, firestore, onAuthStateChanged } from './firebase';
import Login from './Login';
import Register from './Register';
import Success from './Success';
import StripeContainer from './StripeContainer';
import AccountPage from './AccountPage'; 
import VideoPage from './VideoPage'; 
import VideoThumbnail from './VideoThumbnail'; 
import './CustomStyles.css';
import { loadStripe } from '@stripe/stripe-js';

const colors = {
  background: '#FAFAFA',
  primary: '#F0EAE2',
  secondary: '#E0E0E0',
  text: '#333333',
  lightText: '#666666',
  cardBg: '#FFFFFF',
};


const REACT_APP_VERSION = 'client';  
console.log('REACT_APP_VERSION:', REACT_APP_VERSION);



const CategoryPage = ({ category, videos, onSelectVideo, isOpen, lockImageUrl }) => (
  <div className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
    <div className="p-4">
      <h2 className="text-xl font-bold text-[#333333] mb-4">{category}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {videos.map((video, index) => (
          <VideoThumbnail key={index} video={video} onClick={() => onSelectVideo(video)} lockImageUrl={lockImageUrl} />
        ))}
      </div>
    </div>
  </div>
);

const CategoryButton = ({ category, isOpen, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full p-4 mb-2 rounded-xl shadow-md transition-all duration-300 ease-in-out flex items-center justify-between ${isOpen ? 'bg-[#F0EAE2] text-[#333333]' : 'bg-white text-[#333333] hover:bg-[#E0E0E0]'}`}
  >
    <span className="text-lg font-semibold">{category}</span>
    <ChevronDown className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} size={24} />
  </button>
);

const CategoriesPage = ({ categories, videos, onSelectVideo, onAccountClick, lockImageUrl }) => {
  const [openCategory, setOpenCategory] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [nextBillingDate, setNextBillingDate] = useState(null);

  // Automatically open the top category when the component mounts
  useEffect(() => {
    if (categories.length > 0) {
      setOpenCategory(categories[0]);
    }
  }, [categories]);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setSubscriptionStatus(data.subscriptionStatus);

          // Set and compare nextBillingDate
          const currentDate = new Date();
          const fetchedNextBillingDate = data.nextBillingDate ? new Date(data.nextBillingDate.seconds * 1000) : null;
          setNextBillingDate(fetchedNextBillingDate);

          // If the user is inactive but still has access until next billing date, allow access
          if (fetchedNextBillingDate && currentDate < fetchedNextBillingDate) {
            setSubscriptionStatus("Active");
          }
        }
      }
    };
    fetchUserData();
  }, []);

  if (subscriptionStatus === "Inactive" && (!nextBillingDate || new Date() >= nextBillingDate)) {
    return (
      <div className="p-4 bg-[#FAFAFA] min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Votre abonnement est inactif.</h1>
        <button onClick={onAccountClick} className="bg-green-500 text-white py-2 px-4 rounded-lg transition-all hover:bg-green-700">
          Réactivez votre compte pour voir de nouveaux cours de yoga
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-[#FAFAFA] space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#333333]">Catégories de vidéos de yoga</h1>
        <button onClick={onAccountClick} className="flex items-center text-[#333333] hover:text-[#666666] transition-colors">
          <User size={24} className="mr-2" />
          Compte
        </button>
      </div>
      <div>
        {categories.map((category, index) => (
          <div key={category} className="mb-2">
            <CategoryButton
              category={category}
              isOpen={openCategory === category}
              onClick={() => setOpenCategory(openCategory === category ? null : category)}
            />
            <CategoryPage category={category} videos={videos[category]} onSelectVideo={onSelectVideo} isOpen={openCategory === category} lockImageUrl={lockImageUrl} />
          </div>
        ))}
      </div>
    </div>
  );
};

const AppContent = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [userId, setUserId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      if (user) {
        setUserId(user.uid);

        // Fetch the JSON data when the user is authenticated
        const fetchData = async () => {
          try {
            const storageRef = ref(storage, 'viewer_app_data.json');
            const url = await getDownloadURL(storageRef);
            console.log('Fetching JSON from:', url); 
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error('Le réseau a rencontré un problème.');
            }
            const data = await response.json();
            console.log('Données récupérées :', data); 
            setCategories(data.categories);
            setVideos(data.videos);
          } catch (error) {
            console.error('Erreur lors de la récupération du JSON :', error);
          }
        };

        fetchData();  // Fetch the data when user is authenticated
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Erreur lors de la déconnexion :", error);
    }
  };

  const handleSelectVideo = (video) => {
    setSelectedVideo(video);
  };

  const handleBackToCategories = () => {
    setSelectedVideo(null);
    setShowAccount(false);
  };

  const handleAccountClick = () => {
    setShowAccount(true);
  };

  const toggleAuth = () => {
    setIsSignUp(!isSignUp);
  };

  const lockImageUrl = 'https://firebasestorage.googleapis.com/v0/b/yogaapp-7dd5d.appspot.com/o/thumbnails%2FLock_SmallIMG.jpg?alt=media&token=3bb8cba1-b38e-4295-94f7-06c5dc7b3dbe';

  return (
    <Routes>
      <Route path="/success" element={<Success />} />
      <Route path="/collection" element={
        isLoggedIn ? (
          <div className="bg-[#FAFAFA] min-h-screen">
            <div className="p-4 bg-[#F0EAE2] text-[#333333] flex justify-between items-center">
              <h1 className="text-2xl font-bold">Studio en ligne Move Ö Miel</h1>
              <button onClick={handleLogout} className="flex items-center text-[#333333] hover:text-[#666666] transition-colors">
                <User size={24} className="mr-2" />
                Déconnexion
              </button>
            </div>
            {showAccount ? (
              <AccountPage onBack={handleBackToCategories} />
            ) : selectedVideo ? (
              <VideoPage video={selectedVideo} onBack={handleBackToCategories} />
            ) : (
              <CategoriesPage categories={categories} videos={videos} onSelectVideo={handleSelectVideo} onAccountClick={handleAccountClick} lockImageUrl={lockImageUrl} />
            )}
          </div>
        ) : (
          <Navigate to="/" />
        )
      }/>
      <Route path="/register" element={<Register onToggle={toggleAuth} />} />
      <Route path="/login" element={<Login onToggle={toggleAuth} />} />
      <Route path="/stripe" element={<StripeContainer userId={userId} />} />
      <Route path="/" element={
        isLoggedIn ? (
          <Navigate to="/collection" />
        ) : (
          isSignUp ? (
            <Register onToggle={toggleAuth} />
          ) : (
            <Login onToggle={toggleAuth} />
          )
        )
      }/>
    </Routes>
  );
};

const ClientApp = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default ClientApp;
